import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { cloneDeep } from "lodash";
import { of, ReplaySubject, Subject } from "rxjs";
import { concatMap, delay, delayWhen } from "rxjs/operators";
import {
    IAdobeAnalyticsConfig,
    IAdobeFormInteraction,
    IAdobeInteraction,
    IAdobeLinkInteraction,
    IAdobePageLoad,
    IAdobeUserData,
    IAdobeVideoInteraction
} from "../models/adobe-analytics.model";
import { IAnalyticsEvent } from "../models/analytics-event.model";
import { ADOBE_ANALYTICS_CONFIG } from "../utils";

@Injectable()
export class AdobeAnalyticsService {
    private renderer: Renderer2;
    private userData: IAdobeUserData;

    private analyticsLoaded$: ReplaySubject<void> = new ReplaySubject();

    private events$: Subject<IAnalyticsEvent> = new Subject<IAnalyticsEvent>();

    constructor(
        @Inject(ADOBE_ANALYTICS_CONFIG) private config: IAdobeAnalyticsConfig,
        @Inject(DOCUMENT) private document: Document,
        private rendererFactory: RendererFactory2
    ) {
        this.handleEvents();
        this.renderer = this.rendererFactory.createRenderer(null, null);

        if (!this.config.isDevMode) {
            this.validateConfig(this.config);
        }

        this.initAdobeAnalytics(this.config);
    }

    /**
     * Validate the config to make sure it has the required properties
     * @param {IAdobeAnalyticsConfig} config
     */
    private validateConfig(config: IAdobeAnalyticsConfig): void {
        if (!config) {
            throw new Error("Missing Required Adobe Analytics Config");
        } else if (!config.defaultPageLoadData) {
            throw new Error("Missing Required Adobe Analytics Config defaultPageLoadData");
        } else if (config.isDevMode) {
            return;
        } else if (!config.clientDomain || !config.developmentSource || !config.productionSource) {
            throw new Error("Missing Required Adobe Analytics Config Parameters");
        }
    }

    /**
     * Initialize Adobe analytics from given config
     * @param {IAdobeAnalyticsConfig} config
     */
    private initAdobeAnalytics(config: IAdobeAnalyticsConfig): void {
        window.digitalData = window.digitalData || {};

        const defaultUserData = this.getDefaultUserData();

        this.setUserData(defaultUserData);

        if (config.isDevMode) {
            this.analyticsLoaded$.next();
            this.analyticsLoaded$.complete();
        } else {
            const urlHost: string = window.location.host;
            const clientDomain = config.clientDomain;

            const isWww = (host: string): boolean => {
                const regex = new RegExp(/^([a-z]+\:\/{2})?([Ww]{3}\.[\w-]+\.\w+)$/);
                return !!host.match(regex); // make sure it returns boolean
            };

            const isSubdomain = (host: string): boolean => {
                const regex = new RegExp(/^([a-z]+\:\/{2})?([\w-]+\.[\w-]+\.\w+)$/);
                return !!host.match(regex) && !isWww(host); // We don't want to count the www or WWW top level domains as subdomains
            };

            const isClientDomain = (host: string): boolean => {
                return host.indexOf(clientDomain) > -1;
            };

            let src = null;

            if (isSubdomain(urlHost) || !isClientDomain(urlHost)) {
                src = config.developmentSource;
            } else if (isClientDomain(urlHost)) {
                src = config.productionSource;
            }

            if (src) {
                const script = this.renderer.createElement("script");
                this.renderer.appendChild(this.document.head, script);
                script.onload = () => {
                    console.log("Adobe Analytics Ready");
                    this.analyticsLoaded$.next();
                    this.analyticsLoaded$.complete();
                };
                script.src = src;
            }
        }
    }

    // Add default user data by provided cookies
    private getDefaultUserData(): IAdobeUserData[] {
        // Append page load data to default page load data from config
        const dmpId: string = this.getCookieByName("ac_uuid") || "";
        const adobeId: string = this.getCookieByName("ac_mcid") || "";

        let userData = this.config.defaultUserData;

        const idInfo = userData[0].profile[0].id;

        idInfo.dmpID = dmpId;
        idInfo.adobeID = adobeId;

        return userData;
    }

    /**
     * Set global analytics user data
     * @param userData
     */
    public setUserData(userData: any): void {
        this.userData = userData;
    }

    /**
     * Track page load event
     * @param {IAdobePageLoad} adobePageLoad
     * @param {any} userData Optional paramter to override and default or global user data for this page load
     * @returns {Promise<void>}
     */
    public trackPageLoad(adobePageLoad: IAdobePageLoad, userData?: any): void {
        const { pageInfo, category, attributes, journey, product, site } = cloneDeep(adobePageLoad);

        const data = cloneDeep({
            page: {
                pageInfo: {
                    ...(this.config.defaultPageLoadData?.pageInfo || {}),
                    ...(pageInfo || {})
                },
                category: {
                    ...(this.config.defaultPageLoadData?.category || {}),
                    ...category
                },
                attributes: {
                    ...(this.config.defaultPageLoadData?.attributes || {}),
                    ...attributes
                },
                journey: {
                    ...(this.config.defaultPageLoadData?.journey || {}),
                    ...journey
                },
                product: {
                    ...(this.config.defaultPageLoadData?.product || {}),
                    ...product
                },
                site: {
                    ...(this.config.defaultPageLoadData.site || {}),
                    ...site
                },
                modal: {
                    ...(this.config.defaultPageLoadData.modal || {})
                },
                search: {
                    ...(this.config.defaultPageLoadData.search || {})
                }
            },
            user: userData || this.userData || this.config.defaultUserData || []
        });

        const event: IAnalyticsEvent = {
            type: "trackState",
            description: "Page Load",
            data
        };

        this.events$.next(event);
    }

    /**
     * Track page interaction event
     * @param {IAdobeInteraction} adobeInteraction
     * @returns {Promise<void>}
     */
    public trackInteraction(adobeInteraction: IAdobeInteraction): void {
        const { link, component, content, interaction, form, video, journey, mva, menu } = cloneDeep(adobeInteraction);
        // Build window digital data from interaction data
        const data = cloneDeep(window.digitalData) || {};
        data.page = data.page || {};

        data.page.link = {
            name: link && link.name ? link.name : "",
            displayTitle: link && link.title ? link.title : "",
            type: link && link.type ? link.type : "",
            url: link && link.url ? link.url : ""
        };
        data.page.component = {
            type: component && component.type ? component.type : "",
            l10title: component && component.l10title ? component.l10title : "",
            title: component && component.title ? component.title : "",
            name: component && component.name ? component.name : "",
            position: component && component.position ? component.position : ""
        };
        data.page.content = {
            type: content && content.type ? content.type : "",
            title: content && content.title ? content.title : "",
            name: content && content.name ? content.name : ""
        };
        data.page.interaction = {
            type: interaction && interaction.type ? interaction.type : "",
            name: interaction && interaction.name ? interaction.name : ""
        };
        data.page.form = {
            name: form && form.name ? form.name : "",
            category: form && form.category ? form.category : "",
            subCategory: form && form.subCategory ? form.subCategory : "",
            fieldName: form && form.fieldName ? form.fieldName : "",
            fieldValue: form && form.fieldValue ? form.fieldValue : "",
            fieldValues: form && form.fieldValues ? form.fieldValues : "",
            stepName: form && form.stepName ? form.stepName : "",
            stepNumber: form && form.stepNumber ? form.stepNumber : "",
            errors: form && form.errors ? form.errors : ""
        };
        data.page.video = {
            name: video && video.name ? video.name : "",
            id: video && video.id ? video.id : "",
            player: video && video.player ? video.player : "",
            length: video && video.length ? video.length : "",
            action: video && video.action ? video.action : "",
            playback: video && video.playback ? video.playback : "",
            type: video && video.type ? video.type : ""
        };
        data.page.journey = {
            content: journey && journey.content ? journey.content : "",
            patient: journey && journey.patient ? journey.patient : "",
            messageBucket: journey && journey.messageBucket ? journey.messageBucket : ""
        };
        data.page.mva = {
            name: mva && mva.name ? mva.name : "",
            tier: mva && mva.tier ? mva.tier : "",
            type: mva && mva.type ? mva.type : "",
            category: mva && mva.category ? mva.category : ""
        };

        data.page.menu = {
            location: menu && menu.location ? menu.location : "",
            level: menu && menu.level ? menu.level : "",
            label: menu && menu.label ? menu.label : "",
            path: menu && menu.path ? menu.path : ""
        };

        const event: IAnalyticsEvent = {
            type: "trackAction",
            description: "Interaction",
            data
        };
        this.events$.next(event);
    }

    /**
     * Track link interaction event
     * @param {IAdobeInteraction} adobeInteraction
     * @returns {Promise<void>}
     */
    public trackLinkInteraction(adobeInteraction: IAdobeLinkInteraction): void {
        const { link, component, content, interaction, journey, mva } = cloneDeep(adobeInteraction);
        // Build window digital data from interaction data
        const data = cloneDeep(window.digitalData) || {};
        data.page = data.page || {};

        data.page.link = {
            name: link && link.name ? link.name : "",
            displayTitle: link && link.title ? link.title : "",
            type: link && link.type ? link.type : "",
            url: link && link.url ? link.url : ""
        };
        data.page.component = {
            type: component && component.type ? component.type : "",
            l10title: component && component.l10title ? component.l10title : "",
            title: component && component.title ? component.title : "",
            name: component && component.name ? component.name : "",
            position: component && component.position ? component.position : ""
        };
        data.page.content = {
            type: content && content.type ? content.type : "",
            title: content && content.title ? content.title : "",
            name: content && content.name ? content.name : ""
        };
        data.page.interaction = {
            type: interaction && interaction.type ? interaction.type : "",
            name: interaction && interaction.name ? interaction.name : ""
        };
        data.page.journey = {
            content: journey && journey.content ? journey.content : "",
            patient: journey && journey.patient ? journey.patient : "",
            messageBucket: journey && journey.messageBucket ? journey.messageBucket : ""
        };
        data.page.mva = {
            name: mva && mva.name ? mva.name : "",
            tier: mva && mva.tier ? mva.tier : "",
            type: mva && mva.type ? mva.type : "",
            category: mva && mva.category ? mva.category : ""
        };

        delete data.page.video;

        const event: IAnalyticsEvent = {
            type: "trackAction",
            description: "Link Interaction",
            data
        };
        this.events$.next(event);
    }

    /**
     * Track video interaction event
     * @param {IAdobeInteraction} adobeInteraction
     * @returns {Promise<void>}
     */
    public trackVideoInteraction(adobeInteraction: IAdobeVideoInteraction): void {
        const { video, link, component, content, interaction, journey, mva } = cloneDeep(adobeInteraction);
        // Build window digital data from interaction data
        const data = cloneDeep(window.digitalData) || {};
        data.page = data.page || {};

        data.page.video = {
            name: video && video.name ? video.name : "",
            id: video && video.id ? video.id : "",
            player: video && video.player ? video.player : "",
            length: video && video.length ? video.length : "",
            action: video && video.action ? video.action : "",
            playback: video && video.playback ? video.playback : "",
            type: video && video.type ? video.type : ""
        };
        data.page.link = {
            name: link && link.name ? link.name : "",
            displayTitle: link && link.title ? link.title : "",
            type: link && link.type ? link.type : "",
            url: link && link.url ? link.url : ""
        };
        data.page.component = {
            type: component && component.type ? component.type : "",
            l10title: component && component.l10title ? component.l10title : "",
            title: component && component.title ? component.title : "",
            name: component && component.name ? component.name : "",
            position: component && component.position ? component.position : ""
        };
        data.page.content = {
            type: content && content.type ? content.type : "",
            title: content && content.title ? content.title : "",
            name: content && content.name ? content.name : ""
        };
        data.page.interaction = {
            type: interaction && interaction.type ? interaction.type : "",
            name: interaction && interaction.name ? interaction.name : ""
        };
        data.page.journey = {
            content: journey && journey.content ? journey.content : "",
            patient: journey && journey.patient ? journey.patient : "",
            messageBucket: journey && journey.messageBucket ? journey.messageBucket : ""
        };
        data.page.mva = {
            name: mva && mva.name ? mva.name : "",
            tier: mva && mva.tier ? mva.tier : "",
            type: mva && mva.type ? mva.type : "",
            category: mva && mva.category ? mva.category : ""
        };

        const event: IAnalyticsEvent = {
            type: "trackAction",
            description: "Video Interaction",
            data
        };

        this.events$.next(event);
    }

    /**
     * Track form interaction event
     * @param {IAdobeInteraction} adobeInteraction
     * @returns {Promise<void>}
     */
    public trackFormInteraction(adobeInteraction: IAdobeFormInteraction): void {
        const { form, content, interaction, mva } = cloneDeep(adobeInteraction);
        // Build window digital data from interaction data
        const data = cloneDeep(window.digitalData) || {};
        data.page = data.page || {};

        data.page.form = {
            name: form && form.name ? form.name : "",
            category: form && form.category ? form.category : "",
            subCategory: form && form.subCategory ? form.subCategory : "",
            fieldName: form && form.fieldName ? form.fieldName : "",
            fieldValue: form && form.fieldValue ? form.fieldValue : "",
            fieldValues: form && form.fieldValues ? form.fieldValues : "",
            stepName: form && form.stepName ? form.stepName : "",
            stepNumber: form && form.stepNumber ? form.stepNumber : "",
            errors: form && form.errors ? form.errors : ""
        };
        data.page.content = {
            type: content && content.type ? content.type : "",
            title: content && content.title ? content.title : "",
            name: content && content.name ? content.name : ""
        };
        data.page.interaction = {
            type: interaction && interaction.type ? interaction.type : "",
            name: interaction && interaction.name ? interaction.name : ""
        };
        data.page.mva = {
            name: mva && mva.name ? mva.name : "",
            tier: mva && mva.tier ? mva.tier : "",
            type: mva && mva.type ? mva.type : "",
            category: mva && mva.category ? mva.category : ""
        };

        delete data.page.video;

        const event: IAnalyticsEvent = {
            type: "trackAction",
            description: "Form Interaction",
            data
        };

        this.events$.next(event);
    }

    private handleEvents() {
        this.events$
            .pipe(
                delayWhen(() => this.analyticsLoaded$),
                // Make sure that each event is processed in order
                concatMap((event) => {
                    return of(event).pipe(delay(250)); // Prevent sending events at the same time to reach adobe before next interaction
                })
            )
            .subscribe((event: IAnalyticsEvent) => {
                window.digitalData = event.data;

                console.info(event.description, cloneDeep(event.data));

                if (window._satellite) {
                    window._satellite.track(event.type);
                }
            });
    }

    private getCookieByName(name: string): any {
        const escape = (s: string) => {
            return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, "\\$1");
        };
        var match = document.cookie.match(RegExp("(?:^|;\\s*)" + escape(name) + "=([^;]*)"));
        return match ? match[1] : null;
    }
}
